// src/pages/RegisterPage.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { register } from '../store/authSlice';
import { FaEye, FaEyeSlash, FaUser, FaEnvelope, FaLock, FaCalendar, FaSpinner, FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';

/* ── helpers ──────────────────────────────────────────────────────────────── */
const FieldLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">
    {children}
  </label>
);

const FieldError = ({ children }) =>
  children ? <p className="mt-1.5 text-xs text-red-500 font-medium">{children}</p> : null;

/* ── component ────────────────────────────────────────────────────────────── */
const RegisterPage = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, loading, error } = useSelector((s) => s.auth);

  const [formData, setFormData] = useState({
    username: '', email: '', password: '', confirmPassword: '', dob: '',
  });
  const [formErrors,          setFormErrors]          = useState({});
  const [showPassword,        setShowPassword]        = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms,       setAgreedToTerms]       = useState(false);
  const [isSubmitting,        setIsSubmitting]        = useState(false);

  const from      = location.state?.from      || '/explore';
  const videoIds  = location.state?.videoIds;
  const duration  = location.state?.duration;
  const thumbnails = location.state?.thumbnails;

  /* redirect when registered */
  useEffect(() => {
    if (!user) return;
    if (videoIds && duration && thumbnails) {
      navigate('/confirmation', { state: { videoIds, duration, thumbnails } });
    } else {
      navigate(from);
    }
    setFormData({ username: '', email: '', password: '', confirmPassword: '', dob: '' });
  }, [user, navigate, from, videoIds, duration, thumbnails]);

  useEffect(() => { if (error) toast.error(error); }, [error]);

  const validate = () => {
    const e = {};
    if (!formData.username.trim())
      e.username = 'Username is required.';
    else if (formData.username.length < 3)
      e.username = 'Username must be at least 3 characters.';
    else if (!/^[a-zA-Z0-9_]+$/.test(formData.username))
      e.username = 'Letters, numbers, and underscores only.';

    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      e.email = 'Please enter a valid email address.';

    if (!formData.password)
      e.password = 'Password is required.';
    else if (formData.password.length < 6)
      e.password = 'Password must be at least 6 characters.';

    if (!formData.confirmPassword)
      e.confirmPassword = 'Please confirm your password.';
    else if (formData.password !== formData.confirmPassword)
      e.confirmPassword = 'Passwords do not match.';

    if (!formData.dob) {
      e.dob = 'Date of birth is required.';
    } else {
      const dob = new Date(formData.dob);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      if (age < 13) e.dob = 'You must be at least 13 years old.';
    }

    if (!agreedToTerms) e.terms = 'You must agree to the Terms of Service and Privacy Policy.';

    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (formErrors[name]) setFormErrors(p => ({ ...p, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const { confirmPassword, ...payload } = formData;
      await dispatch(register(payload)).unwrap();
      toast.success('Account created! Welcome to Akture.');
    } catch (err) {
      toast.error(err || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls = (field) =>
    `block w-full pl-10 pr-3 py-3 rounded-xl border text-sm text-gray-900 placeholder-gray-400
     focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red transition-all duration-150
     ${formErrors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`;

  /* ── render ── */
  return (
    <>
      {/* Heading */}
      <div className="mb-7">
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight mb-1">Create your account</h1>
        <p className="text-sm text-gray-500">Join Akture to access exclusive video content.</p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-4">

        {/* Username */}
        <div>
          <FieldLabel htmlFor="username">Username</FieldLabel>
          <div className="relative">
            <FaUser className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"/>
            <input id="username" name="username" type="text" autoComplete="username"
              value={formData.username} onChange={handleChange}
              placeholder="Choose a username"
              className={inputCls('username')}
              aria-invalid={!!formErrors.username} disabled={isSubmitting || loading}/>
          </div>
          <FieldError>{formErrors.username}</FieldError>
        </div>

        {/* Email */}
        <div>
          <FieldLabel htmlFor="email">Email Address</FieldLabel>
          <div className="relative">
            <FaEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"/>
            <input id="email" name="email" type="email" autoComplete="email"
              value={formData.email} onChange={handleChange}
              placeholder="you@example.com"
              className={inputCls('email')}
              aria-invalid={!!formErrors.email} disabled={isSubmitting || loading}/>
          </div>
          <FieldError>{formErrors.email}</FieldError>
        </div>

        {/* Date of Birth */}
        <div>
          <FieldLabel htmlFor="dob">Date of Birth</FieldLabel>
          <div className="relative">
            <FaCalendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"/>
            <input id="dob" name="dob" type="date"
              value={formData.dob} onChange={handleChange}
              max={new Date().toISOString().split('T')[0]}
              className={inputCls('dob')}
              aria-invalid={!!formErrors.dob} disabled={isSubmitting || loading}/>
          </div>
          <FieldError>{formErrors.dob}</FieldError>
        </div>

        {/* Password */}
        <div>
          <FieldLabel htmlFor="password">Password</FieldLabel>
          <div className="relative">
            <FaLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"/>
            <input id="password" name="password"
              type={showPassword ? 'text' : 'password'} autoComplete="new-password"
              value={formData.password} onChange={handleChange}
              placeholder="Min. 6 characters"
              className={`${inputCls('password')} pr-11`}
              aria-invalid={!!formErrors.password} disabled={isSubmitting || loading}/>
            <button type="button" onClick={() => setShowPassword(p => !p)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
              aria-label={showPassword ? 'Hide' : 'Show'}>
              {showPassword ? <FaEyeSlash className="text-sm"/> : <FaEye className="text-sm"/>}
            </button>
          </div>
          <FieldError>{formErrors.password}</FieldError>
        </div>

        {/* Confirm Password */}
        <div>
          <FieldLabel htmlFor="confirmPassword">Confirm Password</FieldLabel>
          <div className="relative">
            <FaLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"/>
            <input id="confirmPassword" name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password"
              value={formData.confirmPassword} onChange={handleChange}
              placeholder="Repeat your password"
              className={`${inputCls('confirmPassword')} pr-11`}
              aria-invalid={!!formErrors.confirmPassword} disabled={isSubmitting || loading}/>
            <button type="button" onClick={() => setShowConfirmPassword(p => !p)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
              aria-label={showConfirmPassword ? 'Hide' : 'Show'}>
              {showConfirmPassword ? <FaEyeSlash className="text-sm"/> : <FaEye className="text-sm"/>}
            </button>
          </div>
          <FieldError>{formErrors.confirmPassword}</FieldError>
        </div>

        {/* Terms */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer select-none group">
            <div className={[
              'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all duration-150',
              agreedToTerms ? 'bg-brand-red border-brand-red' : 'border-gray-300 bg-white group-hover:border-gray-400',
            ].join(' ')}>
              <input type="checkbox" checked={agreedToTerms}
                onChange={e => { setAgreedToTerms(e.target.checked); setFormErrors(p => ({...p,terms:''})); }}
                className="sr-only" disabled={isSubmitting || loading}/>
              {agreedToTerms && <FaCheck className="text-white text-[10px]"/>}
            </div>
            <span className="text-xs text-gray-600 leading-relaxed">
              I agree to the{' '}
              <Link to="/terms-of-service" className="text-brand-red font-semibold hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy-policy" className="text-brand-red font-semibold hover:underline">Privacy Policy</Link>
            </span>
          </label>
          <FieldError>{formErrors.terms}</FieldError>
        </div>

        {/* Submit */}
        <button type="submit" disabled={isSubmitting || loading}
          className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-brand-red text-white text-sm font-bold rounded-xl hover:bg-red-700 transition-colors shadow-sm shadow-brand-red/20 disabled:opacity-50 disabled:cursor-not-allowed mt-1">
          {(isSubmitting || loading) ? (
            <><FaSpinner className="animate-spin text-base"/> Creating account…</>
          ) : 'Create account'}
        </button>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-gray-200"/>
        <span className="text-xs text-gray-400 font-medium">or</span>
        <div className="flex-1 h-px bg-gray-200"/>
      </div>

      {/* Sign in link */}
      <p className="text-center text-sm text-gray-600">
        Already have an account?{' '}
        <Link to="/login" state={{ from, videoIds, duration, thumbnails }}
          className="font-bold text-brand-red hover:text-red-700 transition-colors">
          Sign in
        </Link>
      </p>
    </>
  );
};

export default RegisterPage;
